import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from 'sonner';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import PricingLibrary from '@/pages/PricingLibrary';
import Bids from '@/pages/Bids';
import BidWizard from '@/pages/BidWizard';
import BidEditor from '@/pages/BidEditor';
import Leads from '@/pages/Leads';
import Settings from '@/pages/Settings';
import Projects from '@/pages/Projects';
import JobsBoard from '@/pages/JobsBoard';
import Finance from '@/pages/Finance';
import Vendors from '@/pages/Vendors';
import CalendarPage from '@/pages/CalendarPage';
import Payroll from '@/pages/Payroll';
import LearningCenter from '@/pages/LearningCenter';
import PlanReview from '@/pages/PlanReview';
import ProposalPdfView from '@/pages/ProposalPdfView';
import ProposalPortal from '@/pages/ProposalPortal';
import InviteSetPassword from '@/pages/InviteSetPassword';
import { PortalProvider } from '@/contexts/PortalContext';
import PortalLogin from '@/pages/portal/PortalLogin';
import PortalLayout from '@/pages/portal/PortalLayout';
import PortalDashboard from '@/pages/portal/PortalDashboard';
import PortalProgress from '@/pages/portal/PortalProgress';
import PortalPhotos from '@/pages/portal/PortalPhotos';
import PortalDocuments from '@/pages/portal/PortalDocuments';
import PortalSupport from '@/pages/portal/PortalSupport';
import PortalBilling from '@/pages/portal/PortalBilling';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-sky-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-500 animate-pulse" />
        <p className="text-slate-500 text-sm">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/invite/:token" element={<InviteSetPassword />} />
          <Route path="/p/:token" element={<ProposalPortal />} />
          {/* Homeowner Portal */}
          <Route path="/portal/login" element={<PortalProvider><PortalLogin /></PortalProvider>} />
          <Route path="/portal" element={<PortalProvider><PortalLayout /></PortalProvider>}>
            <Route path="dashboard" element={<PortalDashboard />} />
            <Route path="progress" element={<PortalProgress />} />
            <Route path="photos" element={<PortalPhotos />} />
            <Route path="documents" element={<PortalDocuments />} />
            <Route path="billing" element={<PortalBilling />} />
            <Route path="support" element={<PortalSupport />} />
            <Route index element={<Navigate to="dashboard" />} />
          </Route>
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/bids" element={<Bids />} />
            <Route path="/bids/new" element={<BidWizard />} />
            <Route path="/bids/:id/edit" element={<BidWizard />} />
            <Route path="/bids/:id" element={<BidEditor />} />
            <Route path="/bids/:id/pdf" element={<ProposalPdfView />} />
            <Route path="/jobs" element={<JobsBoard />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/vendors" element={<Vendors />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/payroll" element={<Payroll />} />
            <Route path="/learning" element={<LearningCenter />} />
            <Route path="/pricing-library" element={<PricingLibrary />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/plans/:id" element={<PlanReview />} />
            <Route path="/settings" element={<Settings />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
